import { memo, useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownShortWide, faArrowUpShortWide } from "@fortawesome/free-solid-svg-icons";

import Table from "./Table";
import TableHeader from "./TableHeader";
import TableHeading from "./TableHeading";
import TableBody from "./TableBody";
import TableRow from "./TableRow";
import TableData from "./TableData";
import TableFooter from "./TableFooter";
import Loader from "../Loader/Loader";

/* -----------------------------------
   GLOBAL WRAPPER
----------------------------------- */
const TableWrapper = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
  margin-top: 14px;
`;

const StyledTableOuterContainer = styled.section`
  position: relative;
  padding: 0px;
  border-radius: inherit;
`;

const StyledTableContainer = styled.section`
  width: 100%;
  margin: 0 auto;
  padding: 0;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  background: #fff;
  border-radius: inherit;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cfcfcf transparent;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cfcfcf;
    border-radius: 4px;
  }
`;

const TableBuilder = memo(
  ({
    hasFilters = true,
    hasPagination = true,
    tableHeadings = [],
    tableData = [],
    tableFooter = [],
    isLoading = false,
    compact = false,
    striped = false,
    stickyHeader = false,
    rounded = false,
  }) => {
    const [sortList, setSortList] = useState([]);

    // Normalize headings: default empty title to "Actions" and make it non-sticky
    const normalizedHeadings =
      tableHeadings && tableHeadings.length > 0
        ? tableHeadings.map((col) => ({
            ...col,
            title: col.title === "" ? "Actions" : col.title,
            sticky: col.title === "" ? false : col.sticky, // default Actions column not sticky
          }))
        : [
            {
              title: "Actions",
              dataSelector: "actions",
              dataType: "string",
              canSort: false,
              sticky: false,
            },
          ];

    const onClickHeader = (dataSelector, dataType) => {
      setSortList((prev) => {
        const list = [...prev];
        const existing = list.find((i) => i.dataSelector === dataSelector);
        if (!existing) {
          list.push({ dataSelector, dataType, sortType: "ASC" });
        } else {
          if (existing.sortType === "ASC") {
            existing.sortType = "DSC";
          } else {
            return list.filter((i) => i.dataSelector !== dataSelector);
          }
        }
        return [...list];
      });
    };

    const sortTableData = () => {
      if (sortList.length === 0) return tableData;
      const sorted = [...tableData];
      sortList.forEach(({ dataSelector, dataType, sortType }) => {
        const dir = sortType === "DSC" ? -1 : 1;
        sorted.sort((a, b) => {
          let x = a[dataSelector];
          let y = b[dataSelector];
          if (dataType === "number") return dir * (parseFloat(x) - parseFloat(y));
          if (dataType === "boolean") return dir * (Number(x) - Number(y));
          if (dataType === "date" || dataType === "timestamp")
            return dir * (new Date(x) - new Date(y));
          return dir * x?.toString().localeCompare(y?.toString());
        });
      });
      return sorted;
    };

    const finalTableData = sortTableData();

    const composeSortIcon = (selector) => {
      const item = sortList.find((x) => x.dataSelector === selector);
      if (!item) return "";
      return item.sortType === "ASC" ? (
        <FontAwesomeIcon icon={faArrowDownShortWide} style={{ marginLeft: 4 }} />
      ) : (
        <FontAwesomeIcon icon={faArrowUpShortWide} style={{ marginLeft: 4 }} />
      );
    };

    return (
      <TableWrapper>
        <StyledTableOuterContainer
          style={{ borderRadius: rounded ? 10 : "inherit" }}
        >
          <Loader showLoader={isLoading} />

          <StyledTableContainer style={{ borderRadius: rounded ? 10 : "inherit" }}>
            <Table compact={compact}>
              {/* ---------- HEADER ---------- */}
              {normalizedHeadings.length > 0 && (
                <TableHeader>
                  <TableRow>
                    {normalizedHeadings.map(
                      (
                        {
                          title,
                          dataSelector,
                          dataType,
                          canSort = true,
                          shouldShow = true,
                          customheader = false,
                          CustomHeaderComponent,
                          sticky,
                          ...rest
                        },
                        index
                      ) =>
                        shouldShow && (
                          <TableHeading
                            key={`th-${index}`}
                            onClick={() =>
                              canSort && onClickHeader(dataSelector, dataType)
                            }
                            isSticky={stickyHeader && sticky !== false ? "Yes" : "No"}
                            style={{
                              cursor: canSort ? "pointer" : "default",
                            }}
                            {...rest}
                          >
                            {customheader ? (
                              <CustomHeaderComponent />
                            ) : (
                              <>
                                {title}
                                {canSort && composeSortIcon(dataSelector)}
                              </>
                            )}
                          </TableHeading>
                        )
                    )}
                  </TableRow>
                </TableHeader>
              )}

              {/* ---------- BODY ---------- */}
              {finalTableData.length > 0 && (
                <TableBody striped={striped} compact={compact} hoverable>
                  {finalTableData.map((row, rIndex) => (
                    <TableRow key={`tr-${rIndex}`} isSelected={row?.isSelected}>
                      {normalizedHeadings.map(
                        ({ dataSelector, cellrenderer, shouldShow = true, ...rest }, cIndex) =>
                          shouldShow && (
                            <TableData key={`td-${cIndex}`} {...rest} isSelected={row?.isSelected}>
                              {cellrenderer ? cellrenderer(row[dataSelector], row) : row[dataSelector]}
                            </TableData>
                          )
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              )}

              {/* ---------- FOOTER ---------- */}
              {tableFooter?.length > 0 && (
                <TableFooter>
                  <TableRow>
                    {tableFooter.map(({ textAlign, value }, index) => (
                      <TableData textAlign={textAlign} key={`tf-${index}`}>
                        {value}
                      </TableData>
                    ))}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </StyledTableContainer>
        </StyledTableOuterContainer>
      </TableWrapper>
    );
  }
);

export default TableBuilder;
